import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useLoading } from '../../providers/loading_provider'
import { useSnackBar } from '../../providers/snack_bar_provider'
import {
  ApiErrorResponse,
  getKyousouApiClient,
  handleError,
} from '../../services/network'
import FlatButton from '../../components/buttons/flat_button'
import AuthLayout from '../../components/layouts/auth_layout'
import MainBackground from '../../images/main_bg.png'
import { FormField, TextInput, ReadonlyText } from '../../components/form'

type PasswordResetFormProps = {
  password: string
  password_confirmation: string
}

const PasswordResetInputPage: React.FC = () => {
  const { showLoading, hideLoading } = useLoading()
  const [loading, setLoading] = useState<boolean>(true)
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()
  const kyousouApiClient = getKyousouApiClient()
  const pathParams = useParams<{ token: string }>()
  const [email, setEmail] = useState<string | null>(null)

  const { register, handleSubmit, formState, watch } =
    useForm<PasswordResetFormProps>({
      mode: 'onSubmit',
    })

  const { errors } = formState
  const password = watch('password')

  useEffect(() => {
    if (pathParams.token) {
      loadData(pathParams.token)
    }
  }, [pathParams.token])

  const loadData = async (token: string | undefined) => {
    if (!token) return

    try {
      const response = await kyousouApiClient.password.getEmailCreate({
        token: token,
      })

      setEmail(response.data.email)
      setLoading(false)
    } catch (e) {
      handleError(e as ApiErrorResponse, showSnackBar)
      navigate('/password/reset/request')
    }
  }

  const passwordReset = async (data: PasswordResetFormProps) => {
    if (!pathParams.token || !email) {
      showSnackBar(
        [
          'リクエスト内容が不正です。御手数ですが運営までお問い合わせください。',
        ],
        'error'
      )
      return
    }

    showLoading()
    try {
      await kyousouApiClient.password.resetStoreCreate({
        token: pathParams.token,
        email: email,
        password: data.password,
        password_confirmation: data.password_confirmation,
      })
      showSnackBar(['パスワードが正常にリセットされました。'], 'success')
      navigate('/password/reset/complete')
    } catch (e) {
      handleError(e as ApiErrorResponse, showSnackBar)
    } finally {
      hideLoading()
    }
  }

  if (loading) {
    return (
      <AuthLayout>
        <div
          className="min-h-screen flex flex-col justify-center items-center bg-cover bg-center bg-no-repeat p-4"
          style={{
            backgroundImage: `url(${MainBackground})`,
          }}
        >
          <div className="w-full overflow-auto py-24 px-4">
            <div className="bg-white rounded-xl w-full max-w-xl mx-auto p-6">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-main-green"></div>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div
        className="min-h-screen flex flex-col justify-center items-center bg-cover bg-center bg-no-repeat p-4"
        style={{
          backgroundImage: `url(${MainBackground})`,
        }}
      >
        <div className="w-full overflow-auto py-24 px-4">
          <div className="bg-white rounded-xl w-full max-w-xl mx-auto p-6">
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-8 text-center">
                パスワードリセット
              </h2>
            </div>
            <form onSubmit={handleSubmit(passwordReset)}>
              <FormField label="メールアドレス" showTag={false}>
                <ReadonlyText value={email || ''} />
              </FormField>

              <FormField
                label="新しいパスワード"
                required
                error={errors.password?.message}
              >
                <TextInput
                  register={register('password', {
                    required: 'パスワードは必須です',
                    minLength: {
                      value: 8,
                      message: 'パスワードは8文字以上で入力してください',
                    },
                  })}
                  type="password"
                  placeholder="新しいパスワードを入力してください"
                  width="100%"
                />
              </FormField>

              <FormField
                label="新しいパスワード（確認用）"
                required
                className="mt-4"
                error={errors.password_confirmation?.message}
              >
                <TextInput
                  register={register('password_confirmation', {
                    required: 'パスワード（確認用）は必須です',
                    validate: (value) =>
                      value === password || 'パスワードが一致しません',
                  })}
                  type="password"
                  placeholder="新しいパスワードを再度入力してください"
                  width="100%"
                />
              </FormField>

              <FlatButton type="submit" className="w-full mt-8">
                送信
              </FlatButton>
            </form>
          </div>
        </div>
      </div>
    </AuthLayout>
  )
}

export default PasswordResetInputPage
